import {
  generateJacketPatternSIyV0,
  JACKET_V0_FORMULA_VERSION,
} from '@/lib/patterns/jacket/siy_v0';

export const JACKET_V1_FORMULA_VERSION = 'jacket_siy_v1';
export const JACKET_ACTIVE_FORMULA_VERSION = JACKET_V1_FORMULA_VERSION;

const n = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const roundToSixteenth = (value) => Math.round(value / 0.0625) * 0.0625;
const roundToQuarter = (value) => Math.round(value / 0.25) * 0.25;
const rp = (x, y) => ({ x: Number(x.toFixed(4)), y: Number(y.toFixed(4)) });
const fmt = (value) => Number(value).toFixed(4).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
const toSVG = (point) => `${fmt(point.x)} ${fmt(point.y)}`;
const toCanvasPoint = (point) => rp(point.x, -point.y);

const midpoint = (a, b, dx = 0, dy = 0) => rp((a.x + b.x) / 2 + dx, (a.y + b.y) / 2 + dy);

const normalize = (vector, fallback = { x: 1, y: 0 }) => {
  const mag = Math.hypot(vector.x, vector.y);
  if (mag < 1e-8) return fallback;
  return {
    x: vector.x / mag,
    y: vector.y / mag,
  };
};

const angleBisector = (vectorA, vectorB, fallback = { x: 1, y: 0 }) => {
  const a = normalize(vectorA, fallback);
  const b = normalize(vectorB, fallback);
  const summed = { x: a.x + b.x, y: a.y + b.y };
  if (Math.hypot(summed.x, summed.y) < 1e-8) {
    return a;
  }
  return normalize(summed, fallback);
};

const dist = (a, b) => Math.hypot(b.x - a.x, b.y - a.y);

const reflectPointAcrossLine = (point, lineStart, lineEnd) => {
  const vx = lineEnd.x - lineStart.x;
  const vy = lineEnd.y - lineStart.y;
  const denom = (vx * vx) + (vy * vy);
  if (denom < 1e-8) return rp(point.x, point.y);

  const t = (((point.x - lineStart.x) * vx) + ((point.y - lineStart.y) * vy)) / denom;
  const proj = {
    x: lineStart.x + (vx * t),
    y: lineStart.y + (vy * t),
  };

  return rp((2 * proj.x) - point.x, (2 * proj.y) - point.y);
};

const quadPoint = (p0, p1, p2, t) => {
  const inv = 1 - t;
  return rp(
    (inv * inv * p0.x) + (2 * inv * t * p1.x) + (t * t * p2.x),
    (inv * inv * p0.y) + (2 * inv * t * p1.y) + (t * t * p2.y),
  );
};

const quadLength = (p0, p1, p2, steps = 48) => {
  let total = 0;
  let prev = p0;
  for (let index = 1; index <= steps; index += 1) {
    const point = quadPoint(p0, p1, p2, index / steps);
    total += dist(prev, point);
    prev = point;
  }
  return total;
};

const cubicPoint = (p0, p1, p2, p3, t) => {
  const inv = 1 - t;
  return rp(
    (inv ** 3 * p0.x) + (3 * inv * inv * t * p1.x) + (3 * inv * t * t * p2.x) + (t ** 3 * p3.x),
    (inv ** 3 * p0.y) + (3 * inv * inv * t * p1.y) + (3 * inv * t * t * p2.y) + (t ** 3 * p3.y),
  );
};

const cubicLength = (p0, p1, p2, p3, steps = 60) => {
  let total = 0;
  let prev = p0;
  for (let index = 1; index <= steps; index += 1) {
    const point = cubicPoint(p0, p1, p2, p3, index / steps);
    total += dist(prev, point);
    prev = point;
  }
  return total;
};

const sampleQuadratic = (p0, p1, p2, steps = 48) => Array.from({ length: steps + 1 }, (_, index) => (
  quadPoint(p0, p1, p2, index / steps)
));

const polylineLength = (points = []) => points.slice(1).reduce((sum, point, index) => sum + dist(points[index], point), 0);

const getPointAndTangentOnPolyline = (points = [], distanceAlong = 0) => {
  if (!Array.isArray(points) || points.length === 0) {
    return { point: rp(0, 0), tangent: { x: 1, y: 0 } };
  }

  if (points.length === 1) {
    return { point: points[0], tangent: { x: 1, y: 0 } };
  }

  const total = polylineLength(points);
  const clamped = clamp(distanceAlong, 0, total);
  let walked = 0;

  for (let index = 1; index < points.length; index += 1) {
    const prev = points[index - 1];
    const curr = points[index];
    const segLen = dist(prev, curr);
    if (walked + segLen >= clamped) {
      const t = segLen > 1e-9 ? (clamped - walked) / segLen : 0;
      return {
        point: rp(prev.x + ((curr.x - prev.x) * t), prev.y + ((curr.y - prev.y) * t)),
        tangent: normalize({ x: curr.x - prev.x, y: curr.y - prev.y }),
      };
    }
    walked += segLen;
  }

  const last = points[points.length - 1];
  const beforeLast = points[points.length - 2];
  return {
    point: last,
    tangent: normalize({ x: last.x - beforeLast.x, y: last.y - beforeLast.y }),
  };
};

const parseQuadraticPathData = (pathData = '') => {
  const values = String(pathData).match(/-?\d*\.?\d+/g)?.map(Number) || [];
  if (values.length < 6) return null;
  return {
    start: rp(values[0], values[1]),
    control: rp(values[2], values[3]),
    end: rp(values[4], values[5]),
  };
};

const buildNotchSymbolPaths = (point, tangent, type = 'single_t', options = {}) => {
  const { size = 0.18, depth = 0.24, inwardToward = null } = options;
  const t = normalize(tangent, { x: 1, y: 0 });
  let nrm = normalize({ x: -t.y, y: t.x }, { x: 0, y: -1 });

  if (inwardToward) {
    const inwardVector = { x: inwardToward.x - point.x, y: inwardToward.y - point.y };
    if ((nrm.x * inwardVector.x) + (nrm.y * inwardVector.y) < 0) {
      nrm = { x: -nrm.x, y: -nrm.y };
    }
  }

  if (type !== 'single_t') return [];

  const tip = { x: point.x + (nrm.x * depth), y: point.y + (nrm.y * depth) };
  const barHalf = size * 0.5;
  const barA = { x: tip.x - (t.x * barHalf), y: tip.y - (t.y * barHalf) };
  const barB = { x: tip.x + (t.x * barHalf), y: tip.y + (t.y * barHalf) };
  return [`M ${toSVG(point)} L ${toSVG(tip)} M ${toSVG(barA)} L ${toSVG(barB)}`];
};

const splitQuadraticAtT = (p0, p1, p2, t) => {
  const q0 = rp(
    p0.x + ((p1.x - p0.x) * t),
    p0.y + ((p1.y - p0.y) * t),
  );
  const q1 = rp(
    p1.x + ((p2.x - p1.x) * t),
    p1.y + ((p2.y - p1.y) * t),
  );
  const r = rp(
    q0.x + ((q1.x - q0.x) * t),
    q0.y + ((q1.y - q0.y) * t),
  );

  return {
    left: { p0, p1: q0, p2: r },
    right: { p0: r, p1: q1, p2 },
  };
};

const signedLineSide = (point, lineStart, lineEnd) => (
  ((lineEnd.x - lineStart.x) * (point.y - lineStart.y))
  - ((lineEnd.y - lineStart.y) * (point.x - lineStart.x))
);

const findQuadraticLineIntersectionT = (p0, p1, p2, lineStart, lineEnd) => {
  const sampleCount = 160;
  let bestT = 1;
  let bestAbsSide = Infinity;

  let prevT = 0;
  let prevPoint = quadPoint(p0, p1, p2, prevT);
  let prevSide = signedLineSide(prevPoint, lineStart, lineEnd);
  bestAbsSide = Math.abs(prevSide);

  for (let index = 1; index <= sampleCount; index += 1) {
    const nextT = index / sampleCount;
    const nextPoint = quadPoint(p0, p1, p2, nextT);
    const nextSide = signedLineSide(nextPoint, lineStart, lineEnd);
    const nextAbs = Math.abs(nextSide);

    if (nextAbs < bestAbsSide) {
      bestAbsSide = nextAbs;
      bestT = nextT;
    }

    if (Math.abs(prevSide) < 1e-8) return prevT;
    if (Math.abs(nextSide) < 1e-8) return nextT;
    if ((prevSide < 0 && nextSide > 0) || (prevSide > 0 && nextSide < 0)) {
      let low = prevT;
      let high = nextT;
      let lowSide = prevSide;
      for (let refine = 0; refine < 14; refine += 1) {
        const mid = (low + high) / 2;
        const midPoint = quadPoint(p0, p1, p2, mid);
        const midSide = signedLineSide(midPoint, lineStart, lineEnd);
        if (Math.abs(midSide) < 1e-8) return mid;
        if ((lowSide < 0 && midSide > 0) || (lowSide > 0 && midSide < 0)) {
          high = mid;
        } else {
          low = mid;
          lowSide = midSide;
        }
      }
      return (low + high) / 2;
    }

    prevT = nextT;
    prevPoint = nextPoint;
    prevSide = nextSide;
  }

  return bestT;
};

const buildLine = (p1, p2, label, type = 'guide') => ({ p1, p2, label, type });
const mapLineForCanvas = (line) => ({ ...line, p1: toCanvasPoint(line.p1), p2: toCanvasPoint(line.p2) });

const resolveSleeveLengthInfo = (points = {}) => {
  const lengthEnd = midpoint(points.HB, points.TSHF);
  return {
    sleeveLengthEnd: lengthEnd,
    sleeveLengthVisual: dist(points.SLT, lengthEnd),
    sleeveLengthPath: `M ${toSVG(points.SLT)} L ${toSVG(lengthEnd)}`,
  };
};

const normalizePointDeltaMap = (rawPointDeltas = {}) => {
  const source = rawPointDeltas && typeof rawPointDeltas === 'object'
    ? rawPointDeltas
    : {};

  return Object.entries(source).reduce((acc, [pointName, delta]) => {
    if (!pointName || !delta || typeof delta !== 'object') return acc;
    const dx = roundToSixteenth(n(delta.x, 0));
    const dy = roundToSixteenth(n(delta.y, 0));
    if (Math.abs(dx) < 1e-8 && Math.abs(dy) < 1e-8) return acc;
    acc[pointName] = { x: dx, y: dy };
    return acc;
  }, {});
};

const buildMeasurementDistribution = (backPoint, underPoint, topPoint) => {
  const underDrawn = Number(dist(backPoint, underPoint).toFixed(3));
  const topDrawn = Number(dist(backPoint, topPoint).toFixed(3));
  return {
    underDrawn,
    topDrawn,
    totalDrawn: Number((underDrawn + topDrawn).toFixed(3)),
  };
};

const buildSegmentLengthMap = (segments = []) => segments.reduce((acc, segment) => {
  acc[segment.key] = Number(segment.length.toFixed(3));
  return acc;
}, {});

const buildSmoothCurve = (points = [], keys = [], tension = 1 / 6) => {
  if (!Array.isArray(points) || points.length < 2) {
    return { movePoint: points[0] || null, commands: [], segments: [] };
  }

  const commands = [];
  const segments = [];

  for (let index = 0; index < points.length - 1; index += 1) {
    const p0 = points[index - 1] || points[index];
    const p1 = points[index];
    const p2 = points[index + 1];
    const p3 = points[index + 2] || p2;

    const cp1 = rp(
      p1.x + ((p2.x - p0.x) * tension),
      p1.y + ((p2.y - p0.y) * tension),
    );
    const cp2 = rp(
      p2.x - ((p3.x - p1.x) * tension),
      p2.y - ((p3.y - p1.y) * tension),
    );

    commands.push(`C ${toSVG(cp1)} ${toSVG(cp2)} ${toSVG(p2)}`);
    segments.push({
      key: keys[index] || `segment_${index}`,
      start: p1,
      end: p2,
      cp1,
      cp2,
      length: cubicLength(p1, cp1, cp2, p2),
    });
  }

  return {
    movePoint: points[0],
    commands,
    segments,
  };
};

const extendViewBox = (bodyViewBox, allPoints = []) => {
  const xs = allPoints.map((point) => point.x);
  const ys = allPoints.map((point) => point.y);

  const baseMinX = Number(bodyViewBox?.x) || 0;
  const baseMinY = Number(bodyViewBox?.y) || 0;
  const baseMaxX = baseMinX + (Number(bodyViewBox?.width) || 0);
  const baseMaxY = baseMinY + (Number(bodyViewBox?.height) || 0);

  return {
    x: Math.min(baseMinX, ...xs) - 1,
    y: Math.min(baseMinY, ...ys) - 1,
    width: Math.max(baseMaxX, ...xs) - Math.min(baseMinX, ...xs) + 2,
    height: Math.max(baseMaxY, ...ys) - Math.min(baseMinY, ...ys) + 2,
  };
};

const mapNamedPointsToInternal = (namedPoints = []) => namedPoints.reduce((acc, point) => {
  const pointName = String(point?.name || '').trim();
  if (!pointName) return acc;
  acc[pointName] = rp(Number(point.x) || 0, -(Number(point.y) || 0));
  return acc;
}, {});

const findSleeveAnchorOffsetX = (bodyPattern, sleeveWidth = 0, bodyNamedPointMap = {}) => {
  const lp0Mirror = bodyNamedPointMap?.LP0_M;
  if (lp0Mirror) {
    return roundToSixteenth(lp0Mirror.x + 5 + (sleeveWidth / 4));
  }

  const viewBoxX = Number(bodyPattern?.viewBox?.x) || 0;
  const viewBoxWidth = Number(bodyPattern?.viewBox?.width) || 0;
  return roundToSixteenth(viewBoxX + viewBoxWidth + Math.max(8, (sleeveWidth / 2) + 4));
};

const findSleeveAnchorOffsetY = (bodyNamedPointMap = {}, capHeight = 0) => {
  const cf2 = bodyNamedPointMap?.CF2;
  if (cf2) {
    return roundToSixteenth(cf2.y - capHeight);
  }
  return 0;
};

const buildSleeveState = ({
  capHeight,
  sleeveLength,
  distToElbow,
  sleeveHalfBicep,
  sleeveHalfElbow,
  sleeveHalfHem,
  frontPitchPercent,
  backPitchPercent,
  frontUnderarmDx,
  frontUnderarmDy,
  frontCrownDx,
  frontCrownDy,
  backCrownDx,
  backCrownDy,
  backUnderarmDx,
  backUnderarmDy,
  backSeamUpperDx,
  backSeamUpperDy,
  backSeamLowerDx,
  backSeamLowerDy,
  topSeamUpperDx,
  topSeamUpperDy,
  topSeamLowerDx,
  topSeamLowerDy,
  underSeamUpperDx,
  underSeamUpperDy,
  underSeamLowerDx,
  underSeamLowerDy,
  sleeveOffsetX,
  sleeveOffsetY,
  pointDeltas = {},
}) => {
  const applyPointDelta = (pointName, point) => {
    const delta = pointDeltas?.[pointName];
    if (!delta || !point) return point;
    return rp(point.x + n(delta.x, 0), point.y + n(delta.y, 0));
  };

  const innerBicepHalf = sleeveHalfBicep / 2;
  let BiB = rp(-innerBicepHalf + sleeveOffsetX, sleeveOffsetY + capHeight);
  let BiC = rp(sleeveOffsetX, sleeveOffsetY + capHeight);
  let BiF = rp(innerBicepHalf + sleeveOffsetX, sleeveOffsetY + capHeight);
  let SLT = rp(sleeveOffsetX, sleeveOffsetY);
  let BP = rp(BiB.x, sleeveOffsetY + (capHeight * backPitchPercent));
  let FP = rp(BiF.x, sleeveOffsetY + (capHeight * frontPitchPercent));
  let ElB = rp(BiB.x + 0.75, sleeveOffsetY + distToElbow);
  let ElF = rp(ElB.x + sleeveHalfElbow, sleeveOffsetY + distToElbow);
  let HB = rp(ElB.x + 2.25, sleeveOffsetY + sleeveLength);
  let HF = rp(HB.x + sleeveHalfHem, sleeveOffsetY + sleeveLength - 1.25);

  SLT = applyPointDelta('SLT', SLT);
  BP = applyPointDelta('BP', BP);
  FP = applyPointDelta('FP', FP);
  BiB = applyPointDelta('BiB', BiB);
  BiC = applyPointDelta('BiC', BiC);
  BiF = applyPointDelta('BiF', BiF);
  ElB = applyPointDelta('ElB', ElB);
  ElF = applyPointDelta('ElF', ElF);
  HB = applyPointDelta('HB', HB);
  HF = applyPointDelta('HF', HF);

  const backCrownCtrl = midpoint(BP, SLT, backCrownDx, backCrownDy);
  const frontCrownCtrl = midpoint(SLT, FP, frontCrownDx, frontCrownDy);
  const backUnderarmCtrl = midpoint(BP, BiC, backUnderarmDx, ((BiC.y - BP.y) * 0.18) + backUnderarmDy);
  const frontUnderarmCtrl = midpoint(BiC, FP, frontUnderarmDx, -(((BiC.y - FP.y) * 0.18) + frontUnderarmDy));

  const capPathLength = quadLength(BP, backCrownCtrl, SLT)
    + quadLength(SLT, frontCrownCtrl, FP)
    + quadLength(BiC, frontUnderarmCtrl, FP)
    + quadLength(BP, backUnderarmCtrl, BiC);

  const frontInset = 1.25;
  const bicepDir = normalize({ x: BiB.x - BiF.x, y: BiB.y - BiF.y }, { x: -1, y: 0 });
  const elbowDir = normalize({ x: ElB.x - ElF.x, y: ElB.y - ElF.y }, { x: -1, y: 0 });
  const hemDir = normalize({ x: HB.x - HF.x, y: HB.y - HF.y }, { x: -1, y: 0 });

  const USBiF = rp(BiF.x + (bicepDir.x * frontInset), BiF.y + (bicepDir.y * frontInset));
  const USElF = rp(ElF.x + (elbowDir.x * frontInset), ElF.y + (elbowDir.y * frontInset));
  const USHF = rp(HF.x + (hemDir.x * frontInset), HF.y + (hemDir.y * frontInset));

  const intersectionT = clamp(
    findQuadraticLineIntersectionT(BiC, frontUnderarmCtrl, FP, USElF, USBiF),
    0.08,
    0.96,
  );
  const underarmSplit = splitQuadraticAtT(BiC, frontUnderarmCtrl, FP, intersectionT);
  const USFP = underarmSplit.left.p2;
  const TSFP = reflectPointAcrossLine(USFP, FP, BiF);
  const TSBiF = reflectPointAcrossLine(USBiF, FP, BiF);
  const TSElF = reflectPointAcrossLine(USElF, BiF, ElF);
  const TSHF = reflectPointAcrossLine(USHF, ElF, HF);
  const topUnderarmCtrl = reflectPointAcrossLine(underarmSplit.right.p1, FP, BiF);
  const backSeamUpperCtrl = rp(BiB.x + backSeamUpperDx, BiB.y + backSeamUpperDy);
  const backSeamLowerCtrl = midpoint(HB, ElB, backSeamLowerDx, backSeamLowerDy);
  const topSeamUpperCtrl = midpoint(TSFP, TSElF, topSeamUpperDx, topSeamUpperDy);
  const topSeamLowerCtrl = midpoint(TSElF, TSHF, topSeamLowerDx, topSeamLowerDy);
  const underSeamUpperCtrl = midpoint(USFP, USElF, underSeamUpperDx, underSeamUpperDy);
  const underSeamLowerCtrl = midpoint(USElF, USHF, underSeamLowerDx, underSeamLowerDy);
  const sleeveLengthInfo = resolveSleeveLengthInfo({
    SLT,
    HB,
    TSHF,
  });

  return {
    points: {
      SLT,
      BP,
      FP,
      BiB,
      BiC,
      BiF,
      ElB,
      ElF,
      HB,
      HF,
      USBiF,
      USElF,
      USHF,
      USFP,
      TSFP,
      TSBiF,
      TSElF,
      TSHF,
    },
    controls: {
      backCrownCtrl,
      frontCrownCtrl,
      backUnderarmCtrl,
      frontUnderarmCtrl,
      underFrontSplitCtrl: underarmSplit.left.p1,
      frontCapGuideCtrl: underarmSplit.right.p1,
      topFrontSplitCtrl: topUnderarmCtrl,
      backSeamUpperCtrl,
      backSeamLowerCtrl,
      topSeamUpperCtrl,
      topSeamLowerCtrl,
      underSeamUpperCtrl,
      underSeamLowerCtrl,
    },
    capPathLength,
    ...sleeveLengthInfo,
  };
};

const resolveSleeveDepthForTargetLength = ({
  capHeight,
  targetSleeveLength,
  manualDistToElbow,
  sleeveHalfBicep,
  sleeveHalfElbow,
  sleeveHalfHem,
  frontPitchPercent,
  backPitchPercent,
  sleeveOffsetX,
  sleeveOffsetY,
  pointDeltas,
  controlInputs,
}) => {
  const minDepth = Math.max(capHeight + 4, targetSleeveLength - 10);

  const buildStateForDepth = (candidateDepth) => {
    const resolvedDepth = Math.max(capHeight + 3, candidateDepth);
    const candidateAutoDistToElbow = clamp(
      roundToQuarter(capHeight + ((resolvedDepth - capHeight) / 2) - 1.25),
      capHeight + 2,
      resolvedDepth - 1,
    );
    const candidateDistToElbow = manualDistToElbow > 0
      ? clamp(manualDistToElbow, capHeight + 2, resolvedDepth - 1)
      : candidateAutoDistToElbow;
    const candidateState = buildSleeveState({
      capHeight,
      sleeveLength: resolvedDepth,
      distToElbow: candidateDistToElbow,
      sleeveHalfBicep,
      sleeveHalfElbow,
      sleeveHalfHem,
      frontPitchPercent,
      backPitchPercent,
      sleeveOffsetX,
      sleeveOffsetY,
      pointDeltas,
      ...controlInputs,
    });

    return {
      ...candidateState,
      autoDistToElbow: candidateAutoDistToElbow,
      resolvedDistToElbow: candidateDistToElbow,
      resolvedSleeveDepth: Number(resolvedDepth.toFixed(4)),
      sleeveLengthDelta: Math.abs(candidateState.sleeveLengthVisual - targetSleeveLength),
    };
  };

  let lowDepth = minDepth;
  let highDepth = Math.max(lowDepth + 10, targetSleeveLength + 10);
  let lowState = buildStateForDepth(lowDepth);
  let highState = buildStateForDepth(highDepth);

  for (let index = 0; index < 10 && highState.sleeveLengthVisual < targetSleeveLength; index += 1) {
    lowDepth = highDepth;
    lowState = highState;
    highDepth += 4;
    highState = buildStateForDepth(highDepth);
  }

  let bestState = lowState.sleeveLengthDelta <= highState.sleeveLengthDelta ? lowState : highState;

  for (let index = 0; index < 18; index += 1) {
    const midDepth = (lowDepth + highDepth) / 2;
    const midState = buildStateForDepth(midDepth);
    if (midState.sleeveLengthDelta < bestState.sleeveLengthDelta) {
      bestState = midState;
    }

    if (midState.sleeveLengthVisual < targetSleeveLength) {
      lowDepth = midDepth;
      lowState = midState;
    } else {
      highDepth = midDepth;
      highState = midState;
    }
  }

  return bestState;
};

export function generateJacketPatternSIyV1(measurements = {}, controls = {}) {
  const bodyPattern = generateJacketPatternSIyV0(measurements, {
    ...controls,
    formulaVersion: JACKET_V0_FORMULA_VERSION,
    geometryVersion: 'jacket_geom_v0',
  });

  const frontArmholeCurve = n(bodyPattern?.matchingSummary?.frontArmholeCurve, 0);
  const backArmholeCurve = n(bodyPattern?.matchingSummary?.backArmholeCurve, 0);
  const sideArmholeCurve = n(bodyPattern?.matchingSummary?.sideArmholeCurve, 0);
  const totalArmholeCurve = n(bodyPattern?.matchingSummary?.totalArmholeCurve, frontArmholeCurve + backArmholeCurve + sideArmholeCurve);
  const bodyNamedPointMap = mapNamedPointsToInternal(bodyPattern.namedPoints || []);

  const sleeveCapEase = Math.max(0, n(measurements.jacketSleeveCapEase, 1));
  const targetSleeveLength = Math.max(12, n(measurements.jacketSleeveLength, 25.5));
  const sleeveBicepTotal = Math.max(8, n(measurements.jacketSleeveBicep, 15));
  const sleeveElbowTotal = Math.max(6, n(measurements.jacketSleeveElbow, 13));
  const sleeveHemTotal = Math.max(5, n(measurements.jacketSleeveHem, 11));
  const sleeveHalfBicep = sleeveBicepTotal / 2;
  const sleeveHalfElbow = sleeveElbowTotal / 2;
  const sleeveHalfHem = sleeveHemTotal / 2;
  const frontPitchPercent = clamp(n(measurements.sleeveFrontPitchPercent, 0.7), 0.2, 0.8);
  const backPitchPercent = clamp(n(measurements.sleeveBackPitchPercent, 0.35), 0.2, 0.75);
  const targetCapPath = Number((totalArmholeCurve + sleeveCapEase).toFixed(3));
  const sleeveOffsetX = findSleeveAnchorOffsetX(bodyPattern, sleeveBicepTotal, bodyNamedPointMap);
  const sleevePitchRotation = roundToSixteenth(n(
    measurements.sleevePitchRotation,
    measurements.sleevePitchShift,
  ));
  const requestedTopEasePercent = clamp(n(measurements.topSleeveEasePercent, 100), 0, 100);
  const requestedUnderEasePercent = clamp(n(measurements.underSleeveEasePercent, 0), 0, 100);
  const totalEaseWeight = requestedTopEasePercent + requestedUnderEasePercent || 100;
  const normalizedTopEasePercent = requestedTopEasePercent / totalEaseWeight;
  const normalizedUnderEasePercent = requestedUnderEasePercent / totalEaseWeight;

  const minCapHeight = Math.max(3.5, Math.min(targetSleeveLength - 6, totalArmholeCurve * 0.18));
  const maxCapHeight = Math.max(minCapHeight + 0.75, Math.min(targetSleeveLength - 3.5, totalArmholeCurve * 0.5));
  const manualDistToElbow = n(measurements.sleeveDistToElbow, 0);

  const controlInputs = {
    frontUnderarmDx: n(measurements.jacketSleeveFrontUnderarmCtrlDx, 1),
    frontUnderarmDy: n(measurements.jacketSleeveFrontUnderarmCtrlDy, -2),
    frontCrownDx: n(measurements.jacketSleeveFrontCrownCtrlDx, 0.375),
    frontCrownDy: n(measurements.jacketSleeveFrontCrownCtrlDy, -1.75),
    backCrownDx: n(measurements.jacketSleeveBackCrownCtrlDx, -0.25),
    backCrownDy: n(measurements.jacketSleeveBackCrownCtrlDy, -1.25),
    backUnderarmDx: n(measurements.jacketSleeveBackUnderarmCtrlDx, -0.375),
    backUnderarmDy: n(measurements.jacketSleeveBackUnderarmCtrlDy, 0.25),
    backSeamUpperDx: n(measurements.jacketSleeveBackSeamUpperCtrlDx, -0.5),
    backSeamUpperDy: n(measurements.jacketSleeveBackSeamUpperCtrlDy, 0),
    backSeamLowerDx: n(measurements.jacketSleeveBackSeamLowerCtrlDx, -0.25),
    backSeamLowerDy: n(measurements.jacketSleeveBackSeamLowerCtrlDy, 0),
    topSeamUpperDx: n(measurements.jacketSleeveTopSeamUpperCtrlDx, -0.5),
    topSeamUpperDy: n(measurements.jacketSleeveTopSeamUpperCtrlDy, 0),
    topSeamLowerDx: n(measurements.jacketSleeveTopSeamLowerCtrlDx, -0.25),
    topSeamLowerDy: n(measurements.jacketSleeveTopSeamLowerCtrlDy, 0),
    underSeamUpperDx: n(measurements.jacketSleeveUnderSeamUpperCtrlDx, -0.5),
    underSeamUpperDy: n(measurements.jacketSleeveUnderSeamUpperCtrlDy, 0),
    underSeamLowerDx: n(measurements.jacketSleeveUnderSeamLowerCtrlDx, -0.25),
    underSeamLowerDy: n(measurements.jacketSleeveUnderSeamLowerCtrlDy, 0),
  };
  const freePointDeltas = normalizePointDeltaMap(controls?.pointDeltas || {});

  let bestSleeveState = null;
  let bestCapHeight = minCapHeight;
  let bestDelta = Infinity;

  for (let index = 0; index <= 48; index += 1) {
    const candidateCapHeight = minCapHeight + (((maxCapHeight - minCapHeight) * index) / 48);
    const candidateState = resolveSleeveDepthForTargetLength({
      capHeight: candidateCapHeight,
      targetSleeveLength,
      manualDistToElbow,
      sleeveHalfBicep,
      sleeveHalfElbow,
      sleeveHalfHem,
      frontPitchPercent,
      backPitchPercent,
      sleeveOffsetX,
      sleeveOffsetY: findSleeveAnchorOffsetY(bodyNamedPointMap, candidateCapHeight),
      pointDeltas: freePointDeltas,
      controlInputs,
    });
    const delta = Math.abs(candidateState.capPathLength - targetCapPath);
    if (
      delta < bestDelta
      || (Math.abs(delta - bestDelta) < 1e-6 && candidateState.sleeveLengthDelta < (bestSleeveState?.sleeveLengthDelta ?? Infinity))
    ) {
      bestDelta = delta;
      bestSleeveState = candidateState;
      bestCapHeight = candidateCapHeight;
    }
  }

  const {
    points,
    controls: sleeveControls,
    capPathLength,
    autoDistToElbow,
    resolvedDistToElbow,
    resolvedSleeveDepth,
    sleeveLengthEnd,
    sleeveLengthPath,
    sleeveLengthVisual,
  } = bestSleeveState;

  const backSeamSegments = [
    { key: 'hbToElb', start: points.HB, control: sleeveControls.backSeamLowerCtrl, end: points.ElB, length: quadLength(points.HB, sleeveControls.backSeamLowerCtrl, points.ElB) },
    { key: 'elbToBp', start: points.ElB, control: sleeveControls.backSeamUpperCtrl, end: points.BP, length: quadLength(points.ElB, sleeveControls.backSeamUpperCtrl, points.BP) },
  ];
  const topFrontSeamSegments = [
    { key: 'tsfpToTsElf', start: points.TSFP, control: sleeveControls.topSeamUpperCtrl, end: points.TSElF, length: quadLength(points.TSFP, sleeveControls.topSeamUpperCtrl, points.TSElF) },
    { key: 'tsElfToTsHf', start: points.TSElF, control: sleeveControls.topSeamLowerCtrl, end: points.TSHF, length: quadLength(points.TSElF, sleeveControls.topSeamLowerCtrl, points.TSHF) },
  ];
  const underFrontSeamSegments = [
    { key: 'usfpToUsElf', start: points.USFP, control: sleeveControls.underSeamUpperCtrl, end: points.USElF, length: quadLength(points.USFP, sleeveControls.underSeamUpperCtrl, points.USElF) },
    { key: 'usElfToUsHf', start: points.USElF, control: sleeveControls.underSeamLowerCtrl, end: points.USHF, length: quadLength(points.USElF, sleeveControls.underSeamLowerCtrl, points.USHF) },
  ];

  const topSleevePath = [
    `M ${toSVG(points.HB)}`,
    `Q ${toSVG(sleeveControls.backSeamLowerCtrl)} ${toSVG(points.ElB)}`,
    `Q ${toSVG(sleeveControls.backSeamUpperCtrl)} ${toSVG(points.BP)}`,
    `Q ${toSVG(sleeveControls.backCrownCtrl)} ${toSVG(points.SLT)}`,
    `Q ${toSVG(sleeveControls.frontCrownCtrl)} ${toSVG(points.FP)}`,
    `Q ${toSVG(sleeveControls.topFrontSplitCtrl)} ${toSVG(points.TSFP)}`,
    `Q ${toSVG(sleeveControls.topSeamUpperCtrl)} ${toSVG(points.TSElF)}`,
    `Q ${toSVG(sleeveControls.topSeamLowerCtrl)} ${toSVG(points.TSHF)}`,
    'Z',
  ].join(' ');

  const underSleevePath = [
    `M ${toSVG(points.HB)}`,
    `Q ${toSVG(sleeveControls.backSeamLowerCtrl)} ${toSVG(points.ElB)}`,
    `Q ${toSVG(sleeveControls.backSeamUpperCtrl)} ${toSVG(points.BP)}`,
    `Q ${toSVG(sleeveControls.backUnderarmCtrl)} ${toSVG(points.BiC)}`,
    `Q ${toSVG(sleeveControls.underFrontSplitCtrl)} ${toSVG(points.USFP)}`,
    `Q ${toSVG(sleeveControls.underSeamUpperCtrl)} ${toSVG(points.USElF)}`,
    `Q ${toSVG(sleeveControls.underSeamLowerCtrl)} ${toSVG(points.USHF)}`,
    'Z',
  ].join(' ');

  const sleevePath = `${topSleevePath} ${underSleevePath}`;
  const bicepDistribution = buildMeasurementDistribution(points.BiB, points.USBiF, points.TSBiF);
  const elbowDistribution = buildMeasurementDistribution(points.ElB, points.USElF, points.TSElF);
  const hemDistribution = buildMeasurementDistribution(points.HB, points.USHF, points.TSHF);

  const tsFrontSeamSegments = [
    { key: 'fpToTsfp', length: quadLength(points.FP, sleeveControls.topFrontSplitCtrl, points.TSFP) },
    ...topFrontSeamSegments.map(({ key, length }) => ({ key, length })),
  ];
  const usFrontSeamSegments = underFrontSeamSegments.map(({ key, length }) => ({ key, length }));
  const baseTopBackCrownLength = quadLength(points.BP, sleeveControls.backCrownCtrl, points.SLT);
  const frontShoulderSpanLength = quadLength(points.SLT, sleeveControls.frontCrownCtrl, points.FP);
  const frontTopSeamToSplitLength = quadLength(points.FP, sleeveControls.topFrontSplitCtrl, points.TSFP);
  const baseTopFrontCrownLength = frontShoulderSpanLength + frontTopSeamToSplitLength;
  const tsFrontSeamTotal = Number(tsFrontSeamSegments.reduce((sum, segment) => sum + segment.length, 0).toFixed(3));
  const usFrontSeamTotal = Number(usFrontSeamSegments.reduce((sum, segment) => sum + segment.length, 0).toFixed(3));
  const backSeamTotal = Number(backSeamSegments.reduce((sum, segment) => sum + segment.length, 0).toFixed(3));

  const topSleeveEase = sleeveCapEase * normalizedTopEasePercent;
  const underSleeveEase = sleeveCapEase * normalizedUnderEasePercent;
  const clampedPositivePitchRotation = clamp(sleevePitchRotation, 0, baseTopBackCrownLength);
  const clampedNegativePitchRotation = clamp(Math.abs(sleevePitchRotation), 0, frontShoulderSpanLength);
  const resolvedTopBackCrownLength = sleevePitchRotation >= 0
    ? Math.max(0, baseTopBackCrownLength - clampedPositivePitchRotation)
    : baseTopBackCrownLength + clampedNegativePitchRotation;
  const resolvedTopFrontCrownLength = sleevePitchRotation >= 0
    ? baseTopFrontCrownLength + clampedPositivePitchRotation
    : Math.max(frontTopSeamToSplitLength, baseTopFrontCrownLength - clampedNegativePitchRotation);
  const bodyBackJoinDistance = Math.max(0, resolvedTopBackCrownLength - (topSleeveEase / 2));
  const bodyFrontJoinDistance = Math.max(0, resolvedTopFrontCrownLength - (topSleeveEase / 2));

  const curveSegmentById = new Map((bodyPattern.curveSegments || []).map((segment) => [segment.id, segment]));
  const frontArmCurve1 = parseQuadraticPathData(curveSegmentById.get('front-armhole-curve-part-1')?.d);
  const frontArmCurve2 = parseQuadraticPathData(curveSegmentById.get('front-armhole-curve-part-2')?.d);
  const backArmCurve1 = parseQuadraticPathData(curveSegmentById.get('back-armhole-curve-part-1')?.d);
  const backArmCurve2 = parseQuadraticPathData(curveSegmentById.get('back-armhole-curve-part-2')?.d);

  const frontArmholePolyline = frontArmCurve1 && frontArmCurve2
    ? [...sampleQuadratic(frontArmCurve1.start, frontArmCurve1.control, frontArmCurve1.end, 84), ...sampleQuadratic(frontArmCurve2.start, frontArmCurve2.control, frontArmCurve2.end, 84).slice(1)]
    : [];
  const backArmholePolyline = backArmCurve1 && backArmCurve2
    ? [...sampleQuadratic(backArmCurve1.start, backArmCurve1.control, backArmCurve1.end, 84), ...sampleQuadratic(backArmCurve2.start, backArmCurve2.control, backArmCurve2.end, 84).slice(1)]
    : [];
  const totalFrontArmholePolylineLength = polylineLength(frontArmholePolyline);
  const totalBackArmholePolylineLength = polylineLength(backArmholePolyline);

  const sleeveFrontCrownPolyline = sampleQuadratic(points.SLT, sleeveControls.frontCrownCtrl, points.FP, 84);
  const sleeveBackCrownPolyline = sampleQuadratic(points.BP, sleeveControls.backCrownCtrl, points.SLT, 84).reverse();
  const sleeveShoulderNotch = sleevePitchRotation >= 0
    ? getPointAndTangentOnPolyline(sleeveBackCrownPolyline, clampedPositivePitchRotation)
    : getPointAndTangentOnPolyline(sleeveFrontCrownPolyline, clampedNegativePitchRotation);

  const bodyFrontJoinNotch = getPointAndTangentOnPolyline(frontArmholePolyline, bodyFrontJoinDistance);
  const bodyBackJoinNotch = getPointAndTangentOnPolyline(backArmholePolyline, bodyBackJoinDistance);
  const bodyFrontShoulderNotch = getPointAndTangentOnPolyline(frontArmholePolyline, Math.max(0, sleevePitchRotation));
  const bodyBackShoulderNotch = getPointAndTangentOnPolyline(backArmholePolyline, Math.max(0, -sleevePitchRotation));
  const activeBodySleevePitchNotch = sleevePitchRotation >= 0 ? bodyFrontShoulderNotch : bodyBackShoulderNotch;

  const sleeveInteriorHint = midpoint(points.BiC, points.ElB);
  const frontBodyInteriorHint = midpoint(bodyNamedPointMap.CF2 || points.FP, bodyNamedPointMap.FCPS || points.FP);
  const backBodyInteriorHint = midpoint(bodyNamedPointMap.CB4 || points.BP, bodyNamedPointMap.BCPS || points.BP);
  const sleeveBackJoinTangent = angleBisector(
    { x: points.SLT.x - points.BP.x, y: points.SLT.y - points.BP.y },
    { x: points.ElB.x - points.BP.x, y: points.ElB.y - points.BP.y },
  );
  const sleeveTopFrontJoinTangent = angleBisector(
    { x: points.FP.x - points.TSFP.x, y: points.FP.y - points.TSFP.y },
    { x: points.TSElF.x - points.TSFP.x, y: points.TSElF.y - points.TSFP.y },
  );
  const sleeveUnderFrontJoinTangent = angleBisector(
    { x: points.BiC.x - points.USFP.x, y: points.BiC.y - points.USFP.y },
    { x: points.USElF.x - points.USFP.x, y: points.USElF.y - points.USFP.y },
  );
  const frontShoulderBisector = angleBisector(
    { x: (bodyNamedPointMap.CF2?.x || bodyFrontShoulderNotch.point.x) - bodyFrontShoulderNotch.point.x, y: (bodyNamedPointMap.CF2?.y || bodyFrontShoulderNotch.point.y) - bodyFrontShoulderNotch.point.y },
    bodyFrontShoulderNotch.tangent,
  );
  const backShoulderBisector = angleBisector(
    { x: (bodyNamedPointMap.CB2?.x || bodyBackShoulderNotch.point.x) - bodyBackShoulderNotch.point.x, y: (bodyNamedPointMap.CB2?.y || bodyBackShoulderNotch.point.y) - bodyBackShoulderNotch.point.y },
    bodyBackShoulderNotch.tangent,
  );

  const notchMarks = [
    {
      id: 'jacket-notch-top-slt-single-t',
      piece: 'topSleeve',
      role: 'shoulder_anchor',
      type: 'single_t',
      paths: buildNotchSymbolPaths(sleeveShoulderNotch.point, sleeveShoulderNotch.tangent, 'single_t', { size: 0.18, depth: 0.24, inwardToward: sleeveInteriorHint }),
    },
    {
      id: 'jacket-notch-front-join-single-t',
      piece: 'front',
      role: 'front_join',
      type: 'single_t',
      paths: buildNotchSymbolPaths(bodyFrontJoinNotch.point, bodyFrontJoinNotch.tangent, 'single_t', { size: 0.2, depth: 0.28, inwardToward: frontBodyInteriorHint }),
    },
    {
      id: 'jacket-notch-back-join-single-t',
      piece: 'back',
      role: 'back_join',
      type: 'single_t',
      paths: buildNotchSymbolPaths(bodyBackJoinNotch.point, bodyBackJoinNotch.tangent, 'single_t', { size: 0.2, depth: 0.28, inwardToward: backBodyInteriorHint }),
    },
  ];

  const sleeveConstructionLines = [
    buildLine(points.BiB, points.BiF, 'Bicep Line'),
    buildLine(points.ElB, points.ElF, 'Elbow Line'),
    buildLine(points.HB, points.HF, 'Hem Line'),
    buildLine(points.FP, points.BiF, 'FP Axis'),
    buildLine(points.BiF, points.ElF, 'BiF Axis'),
    buildLine(points.ElF, points.HF, 'HF Axis'),
    buildLine(points.USElF, points.USBiF, 'US Split Line'),
  ];

  const topSleeveConstructionLines = [
    {
      id: 'jacket-top-sleeve-length-measurement',
      p1: points.SLT,
      p2: sleeveLengthEnd,
      label: 'Sleeve Length',
      type: 'measurement',
      showLabel: true,
      showLabelInPreview: false,
      showLabelInPdf: false,
      value: Number(sleeveLengthVisual.toFixed(3)),
      labelText: `${Number(sleeveLengthVisual || 0).toFixed(2)}\"`,
      labelOffset: 0.26,
      labelOffsetMm: 3.4,
    },
    buildLine(points.BiB, points.TSBiF, 'Top Sleeve Bicep Line', 'measurement'),
    buildLine(points.ElB, points.TSElF, 'Top Sleeve Elbow Line', 'measurement'),
  ];

  const underSleeveConstructionLines = [
    buildLine(points.BiB, points.USBiF, 'Under Sleeve Bicep Line', 'measurement'),
    buildLine(points.ElB, points.USElF, 'Under Sleeve Elbow Line', 'measurement'),
  ];

  const sleeveCurveSegments = [
    {
      id: 'sleeve-back-crown-curve',
      label: 'Sleeve Back Crown (BP→SLT)',
      d: `M ${toSVG(points.BP)} Q ${toSVG(sleeveControls.backCrownCtrl)} ${toSVG(points.SLT)}`,
      color: '#b91c1c',
    },
    {
      id: 'sleeve-front-crown-curve',
      label: 'Sleeve Front Crown (SLT→FP)',
      d: `M ${toSVG(points.SLT)} Q ${toSVG(sleeveControls.frontCrownCtrl)} ${toSVG(points.FP)}`,
      color: '#1d4ed8',
    },
    {
      id: 'sleeve-back-underarm-curve',
      label: 'Sleeve Back Underarm (BP→BiC)',
      d: `M ${toSVG(points.BP)} Q ${toSVG(sleeveControls.backUnderarmCtrl)} ${toSVG(points.BiC)}`,
      color: '#b45309',
    },
    {
      id: 'sleeve-front-underarm-curve',
      label: 'Sleeve Front Underarm (BiC→FP)',
      d: `M ${toSVG(points.BiC)} Q ${toSVG(sleeveControls.frontUnderarmCtrl)} ${toSVG(points.FP)}`,
      color: '#2563eb',
    },
    {
      id: 'sleeve-front-cap-guide-curve',
      label: 'Sleeve Front Cap Guide (USFP→FP)',
      d: `M ${toSVG(points.USFP)} Q ${toSVG(sleeveControls.frontCapGuideCtrl)} ${toSVG(points.FP)}`,
      color: '#475569',
      strokeWidth: '0.12',
      opacity: 0.82,
      strokeDasharray: '0.2 0.14',
      guideOnly: true,
    },
    {
      id: 'sleeve-back-seam-lower-curve',
      label: 'Sleeve Back Seam Lower (HB→ElB)',
      d: `M ${toSVG(points.HB)} Q ${toSVG(sleeveControls.backSeamLowerCtrl)} ${toSVG(points.ElB)}`,
      color: '#0f766e',
    },
    {
      id: 'sleeve-back-seam-upper-curve',
      label: 'Sleeve Back Seam Upper (ElB→BP)',
      d: `M ${toSVG(points.ElB)} Q ${toSVG(sleeveControls.backSeamUpperCtrl)} ${toSVG(points.BP)}`,
      color: '#115e59',
    },
    {
      id: 'sleeve-top-front-seam-upper-curve',
      label: 'Top Sleeve Front Seam Upper (TSFP→TSElF)',
      d: `M ${toSVG(points.TSFP)} Q ${toSVG(sleeveControls.topSeamUpperCtrl)} ${toSVG(points.TSElF)}`,
      color: '#7c3aed',
    },
    {
      id: 'sleeve-top-front-seam-lower-curve',
      label: 'Top Sleeve Front Seam Lower (TSElF→TSHF)',
      d: `M ${toSVG(points.TSElF)} Q ${toSVG(sleeveControls.topSeamLowerCtrl)} ${toSVG(points.TSHF)}`,
      color: '#6d28d9',
    },
    {
      id: 'sleeve-under-front-seam-upper-curve',
      label: 'Under Sleeve Front Seam Upper (USFP→USElF)',
      d: `M ${toSVG(points.USFP)} Q ${toSVG(sleeveControls.underSeamUpperCtrl)} ${toSVG(points.USElF)}`,
      color: '#0f766e',
    },
    {
      id: 'sleeve-under-front-seam-lower-curve',
      label: 'Under Sleeve Front Seam Lower (USElF→USHF)',
      d: `M ${toSVG(points.USElF)} Q ${toSVG(sleeveControls.underSeamLowerCtrl)} ${toSVG(points.USHF)}`,
      color: '#059669',
    },
  ];

  const sleeveCurveHandles = [
    {
      id: 'jacket-sleeve-back-crown-ctrl',
      curveId: 'sleeve-back-crown-curve',
      label: 'Sleeve Back Crown Control',
      xKey: 'jacketSleeveBackCrownCtrlDx',
      yKey: 'jacketSleeveBackCrownCtrlDy',
      point: toCanvasPoint(sleeveControls.backCrownCtrl),
      segmentStart: toCanvasPoint(points.BP),
      segmentEnd: toCanvasPoint(points.SLT),
      color: '#b91c1c',
    },
    {
      id: 'jacket-sleeve-front-crown-ctrl',
      curveId: 'sleeve-front-crown-curve',
      label: 'Sleeve Front Crown Control',
      xKey: 'jacketSleeveFrontCrownCtrlDx',
      yKey: 'jacketSleeveFrontCrownCtrlDy',
      point: toCanvasPoint(sleeveControls.frontCrownCtrl),
      segmentStart: toCanvasPoint(points.SLT),
      segmentEnd: toCanvasPoint(points.FP),
      color: '#1d4ed8',
    },
    {
      id: 'jacket-sleeve-back-underarm-ctrl',
      curveId: 'sleeve-back-underarm-curve',
      label: 'Sleeve Back Underarm Control',
      xKey: 'jacketSleeveBackUnderarmCtrlDx',
      yKey: 'jacketSleeveBackUnderarmCtrlDy',
      point: toCanvasPoint(sleeveControls.backUnderarmCtrl),
      segmentStart: toCanvasPoint(points.BP),
      segmentEnd: toCanvasPoint(points.BiC),
      color: '#b45309',
    },
    {
      id: 'jacket-sleeve-front-underarm-ctrl',
      curveId: 'sleeve-front-underarm-curve',
      label: 'Sleeve Front Underarm Control',
      xKey: 'jacketSleeveFrontUnderarmCtrlDx',
      yKey: 'jacketSleeveFrontUnderarmCtrlDy',
      point: toCanvasPoint(sleeveControls.frontUnderarmCtrl),
      segmentStart: toCanvasPoint(points.BiC),
      segmentEnd: toCanvasPoint(points.FP),
      color: '#2563eb',
    },
    {
      id: 'jacket-sleeve-back-seam-lower-ctrl',
      curveId: 'sleeve-back-seam-lower-curve',
      label: 'Sleeve Back Seam Lower Control',
      xKey: 'jacketSleeveBackSeamLowerCtrlDx',
      yKey: 'jacketSleeveBackSeamLowerCtrlDy',
      point: toCanvasPoint(sleeveControls.backSeamLowerCtrl),
      segmentStart: toCanvasPoint(points.HB),
      segmentEnd: toCanvasPoint(points.ElB),
      color: '#0f766e',
    },
    {
      id: 'jacket-sleeve-back-seam-upper-ctrl',
      curveId: 'sleeve-back-seam-upper-curve',
      label: 'Sleeve Back Seam Upper Control',
      xKey: 'jacketSleeveBackSeamUpperCtrlDx',
      yKey: 'jacketSleeveBackSeamUpperCtrlDy',
      point: toCanvasPoint(sleeveControls.backSeamUpperCtrl),
      segmentStart: toCanvasPoint(points.ElB),
      segmentEnd: toCanvasPoint(points.BP),
      color: '#115e59',
    },
    {
      id: 'jacket-sleeve-top-seam-upper-ctrl',
      curveId: 'sleeve-top-front-seam-upper-curve',
      label: 'Top Sleeve Front Seam Upper Control',
      xKey: 'jacketSleeveTopSeamUpperCtrlDx',
      yKey: 'jacketSleeveTopSeamUpperCtrlDy',
      point: toCanvasPoint(sleeveControls.topSeamUpperCtrl),
      segmentStart: toCanvasPoint(points.TSFP),
      segmentEnd: toCanvasPoint(points.TSElF),
      color: '#7c3aed',
    },
    {
      id: 'jacket-sleeve-top-seam-lower-ctrl',
      curveId: 'sleeve-top-front-seam-lower-curve',
      label: 'Top Sleeve Front Seam Lower Control',
      xKey: 'jacketSleeveTopSeamLowerCtrlDx',
      yKey: 'jacketSleeveTopSeamLowerCtrlDy',
      point: toCanvasPoint(sleeveControls.topSeamLowerCtrl),
      segmentStart: toCanvasPoint(points.TSElF),
      segmentEnd: toCanvasPoint(points.TSHF),
      color: '#6d28d9',
    },
    {
      id: 'jacket-sleeve-under-seam-upper-ctrl',
      curveId: 'sleeve-under-front-seam-upper-curve',
      label: 'Under Sleeve Front Seam Upper Control',
      xKey: 'jacketSleeveUnderSeamUpperCtrlDx',
      yKey: 'jacketSleeveUnderSeamUpperCtrlDy',
      point: toCanvasPoint(sleeveControls.underSeamUpperCtrl),
      segmentStart: toCanvasPoint(points.USFP),
      segmentEnd: toCanvasPoint(points.USElF),
      color: '#0f766e',
    },
    {
      id: 'jacket-sleeve-under-seam-lower-ctrl',
      curveId: 'sleeve-under-front-seam-lower-curve',
      label: 'Under Sleeve Front Seam Lower Control',
      xKey: 'jacketSleeveUnderSeamLowerCtrlDx',
      yKey: 'jacketSleeveUnderSeamLowerCtrlDy',
      point: toCanvasPoint(sleeveControls.underSeamLowerCtrl),
      segmentStart: toCanvasPoint(points.USElF),
      segmentEnd: toCanvasPoint(points.USHF),
      color: '#059669',
    },
  ];

  const sleeveNamedPoints = [
    'SLT', 'BP', 'FP', 'BiB', 'BiC', 'BiF', 'ElB', 'ElF', 'HB', 'HF',
    'USElF', 'USHF', 'USFP', 'TSFP', 'TSElF', 'TSHF',
  ].map((name) => ({ name, ...toCanvasPoint(points[name]), piece: 'sleeve' }));
  sleeveNamedPoints.push(
    { name: 'FRONT_JOIN_NOTCH', ...toCanvasPoint(bodyFrontJoinNotch.point), piece: 'front' },
    { name: 'BACK_JOIN_NOTCH', ...toCanvasPoint(bodyBackJoinNotch.point), piece: 'back' },
    { name: 'SLEEVE_SHOULDER_NOTCH', ...toCanvasPoint(sleeveShoulderNotch.point), piece: 'sleeve' },
  );

  const sleeveNoteLines = [
    'J-SIY-V1 sleeve: cap target = jacket body armhole + Sleeve Cap Ease, solved by cap-height iteration.',
    'Sleeve Length now resolves to the straight line from SLT to the midpoint of HB→TSHF.',
    'Base sleeve widths draft from half values internally; TS + US drawn widths are reported as the full measurement outcome.',
    'USFP is solved on the actual BiC→FP curve, then the mirrored TS seam is reflected from FP–BiF / BiF–ElF / ElF–HF axis lines.',
    'Lower sleeve joins remain straight in V1 unless explicitly changed later.',
  ];

  const matchingSummary = {
    ...(bodyPattern?.matchingSummary || {}),
    sleeveCapEase: Number(sleeveCapEase.toFixed(3)),
    sleeveCapHeight: Number(bestCapHeight.toFixed(3)),
    sleeveCapPathLength: Number(capPathLength.toFixed(3)),
    sleeveCapTarget: Number(targetCapPath.toFixed(3)),
    sleeveDistToElbowAuto: Number(autoDistToElbow.toFixed(3)),
    sleeveDistToElbow: Number(resolvedDistToElbow.toFixed(3)),
    jacketSleeveLengthInput: Number(targetSleeveLength.toFixed(3)),
    jacketSleeveLengthDepthResolved: Number(resolvedSleeveDepth.toFixed(3)),
    jacketSleeveLengthMidpointDrawn: Number(sleeveLengthVisual.toFixed(3)),
    jacketSleeveBicepInput: Number(sleeveBicepTotal.toFixed(3)),
    jacketSleeveElbowInput: Number(sleeveElbowTotal.toFixed(3)),
    jacketSleeveHemInput: Number(sleeveHemTotal.toFixed(3)),
    jacketSleeveBicepHalfBase: Number(sleeveHalfBicep.toFixed(3)),
    jacketSleeveElbowHalfBase: Number(sleeveHalfElbow.toFixed(3)),
    jacketSleeveHemHalfBase: Number(sleeveHalfHem.toFixed(3)),
    jacketSleeveTopBicepDrawn: bicepDistribution.topDrawn,
    jacketSleeveUnderBicepDrawn: bicepDistribution.underDrawn,
    jacketSleeveBicepTotalDrawn: bicepDistribution.totalDrawn,
    jacketSleeveTopElbowDrawn: elbowDistribution.topDrawn,
    jacketSleeveUnderElbowDrawn: elbowDistribution.underDrawn,
    jacketSleeveElbowTotalDrawn: elbowDistribution.totalDrawn,
    jacketSleeveTopHemDrawn: hemDistribution.topDrawn,
    jacketSleeveUnderHemDrawn: hemDistribution.underDrawn,
    jacketSleeveHemTotalDrawn: hemDistribution.totalDrawn,
    jacketSleeveTsFrontSeamTotal: tsFrontSeamTotal,
    jacketSleeveUsFrontSeamTotal: usFrontSeamTotal,
    jacketSleeveBackSeamTotal: backSeamTotal,
    sleevePitchRotation: Number(sleevePitchRotation.toFixed(3)),
    sleevePitchShift: Number(sleevePitchRotation.toFixed(3)),
    topSleeveEasePercent: Number((normalizedTopEasePercent * 100).toFixed(3)),
    underSleeveEasePercent: Number((normalizedUnderEasePercent * 100).toFixed(3)),
    topSleeveEaseValue: Number(topSleeveEase.toFixed(3)),
    underSleeveEaseValue: Number(underSleeveEase.toFixed(3)),
    sleeveBodyBackJoinDistance: Number(bodyBackJoinDistance.toFixed(3)),
    sleeveBodyFrontJoinDistance: Number(bodyFrontJoinDistance.toFixed(3)),
    sleeveTopBackCrownLength: Number(resolvedTopBackCrownLength.toFixed(3)),
    sleeveTopFrontCrownLength: Number(resolvedTopFrontCrownLength.toFixed(3)),
    sleeveTopBackCrownLengthBase: Number(baseTopBackCrownLength.toFixed(3)),
    sleeveTopFrontCrownLengthBase: Number(baseTopFrontCrownLength.toFixed(3)),
    sleeveUnderJoinLength: Number((quadLength(points.BP, sleeveControls.backUnderarmCtrl, points.BiC) + quadLength(points.BiC, sleeveControls.underFrontSplitCtrl, points.USFP)).toFixed(3)),
    sleeveBodyBackShoulderSegment: Number(bodyBackJoinDistance.toFixed(3)),
    sleeveBodyBackSideSegment: Number((Math.max(0, totalBackArmholePolylineLength - bodyBackJoinDistance)).toFixed(3)),
    sleeveBodyFrontShoulderSegment: Number(bodyFrontJoinDistance.toFixed(3)),
    sleeveBodyFrontSideSegment: Number((Math.max(0, totalFrontArmholePolylineLength - bodyFrontJoinDistance)).toFixed(3)),
    ...Object.fromEntries(Object.entries(buildSegmentLengthMap(backSeamSegments)).map(([key, value]) => [`jacketSleeveBackSeam_${key}`, value])),
    ...Object.fromEntries(Object.entries(buildSegmentLengthMap(tsFrontSeamSegments)).map(([key, value]) => [`jacketSleeveTsFrontSeam_${key}`, value])),
    ...Object.fromEntries(Object.entries(buildSegmentLengthMap(usFrontSeamSegments)).map(([key, value]) => [`jacketSleeveUsFrontSeam_${key}`, value])),
    formulaVersion: controls.formulaVersion || JACKET_ACTIVE_FORMULA_VERSION,
    geometryVersion: controls.geometryVersion || 'jacket_geom_v1',
  };

  const allSleevePoints = [
    ...Object.values(points),
    ...Object.values(sleeveControls),
  ];

  return {
    ...bodyPattern,
    namedPoints: [...(bodyPattern.namedPoints || []), ...sleeveNamedPoints],
    pointReferences: [...(bodyPattern.pointReferences || []), ...sleeveNoteLines],
    curveSegments: [...(bodyPattern.curveSegments || []), ...sleeveCurveSegments],
    curveHandles: [...(bodyPattern.curveHandles || []), ...sleeveCurveHandles],
    notchMarks,
    sleevePath,
    topSleevePath,
    underSleevePath,
    sleeveConstructionLines: sleeveConstructionLines.map(mapLineForCanvas),
    topSleeveConstructionLines: topSleeveConstructionLines.map(mapLineForCanvas),
    underSleeveConstructionLines: underSleeveConstructionLines.map(mapLineForCanvas),
    sleeveLengthPath,
    sleeveLengthGuide: mapLineForCanvas({
      id: 'jacket-top-sleeve-length-measurement',
      p1: points.SLT,
      p2: sleeveLengthEnd,
      label: 'Sleeve Length',
      type: 'measurement',
      showLabel: true,
      showLabelInPreview: false,
      showLabelInPdf: false,
      value: Number(sleeveLengthVisual.toFixed(3)),
      labelText: `${Number(sleeveLengthVisual || 0).toFixed(2)}\"`,
      labelOffset: 0.26,
      labelOffsetMm: 3.4,
      piece: 'topSleeve',
    }),
    sleeveOnBodyPlacement: {
      sourceAnchor: toCanvasPoint(points.USFP),
      targetAnchor: toCanvasPoint(bodyFrontJoinNotch.point),
      sourcePitchPoint: toCanvasPoint(sleeveShoulderNotch.point),
      targetPitchPoint: toCanvasPoint(activeBodySleevePitchNotch.point),
    },
    matchingSummary,
    viewBox: extendViewBox(bodyPattern.viewBox, allSleevePoints),
    formulaVersion: controls.formulaVersion || JACKET_ACTIVE_FORMULA_VERSION,
    geometryVersion: controls.geometryVersion || 'jacket_geom_v1',
    sleeveMeta: {
      bodyArmhole: Number(totalArmholeCurve.toFixed(3)),
      frontArmhole: Number(frontArmholeCurve.toFixed(3)),
      backArmhole: Number(backArmholeCurve.toFixed(3)),
      sideArmhole: Number(sideArmholeCurve.toFixed(3)),
    },
  };
}